<template>
    <div
        class="w-uploader"
        :class="{ 'is-dragover': isDragOver }"
        @click="triggerSelect"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
    >
        <input
            type="file"
            ref="fileInput"
            multiple
            accept="image/*"
            style="display: none"
            @change="onFileChange"
        >
        
        <template v-if="!isUploading">
            <img :src="$static('wd-task/img_add_picture.png')" alt="">
            <div class="text-1">点击或将图片/文件夹拖放到此处</div>
            <div class="text-2">最多200张图片，每张不超过20MB</div>
            <div class="text-2">如电脑性能偏低，建议单次批量上传50张以内，以免浏览器卡顿上传失败</div>
        </template>
        
        <template v-else>
            <div class="progress-box">
                <div class="text-1">正在处理并上传 ({{ currentUploadIndex }} / {{ totalUploadFiles }})</div>
                <div class="progress-bar">
                    <div class="progress-inner" :style="{ width: uploadProgress + '%' }"></div>
                </div>
                <div class="text-2">{{ uploadProgress }}%</div>
            </div>
        </template>
    </div>
</template>

<script>
import {$http} from '@/repo/request';
import api from "@/repo/api";

export default {
    name: 'WUploader',
    props: {
        jobId: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            isDragOver: false,
            isUploading: false,
            totalUploadFiles: 0,
            currentUploadIndex: 0,
            uploadProgress: 0,
        };
    },
    methods: {
        triggerSelect() {
            if (this.isUploading) return;
            this.$refs.fileInput.click();
        },
        
        onFileChange(e) {
            const files = Array.from(e.target.files);
            this.handleFiles(files);
            e.target.value = '';
        },
        
        onDragOver() {
            if (this.isUploading) return;
            this.isDragOver = true;
        },
        
        onDragLeave() {
            this.isDragOver = false;
        },
        
        async onDrop(e) {
            this.isDragOver = false;
            if (this.isUploading) return;
            
            const items = e.dataTransfer.items;
            if (items) {
                // 拖拽文件夹解析
                const files = await this.getFilesFromDataTransfer(items);
                this.handleFiles(files);
            } else {
                this.handleFiles(Array.from(e.dataTransfer.files));
            }
        },
        
        async getFilesFromDataTransfer(items) {
            const files = [];
            for (let i = 0; i < items.length; i++) {
                const item = items[i].webkitGetAsEntry();
                if (item) {
                    await this.traverseFileTree(item, files);
                }
            }
            return files;
        },
        
        traverseFileTree(item, files) {
            return new Promise((resolve) => {
                if (item.isFile) {
                    item.file((file) => {
                        // 注意：这里先把所有文件都收集起来，后续在 handleFiles 统一做严格校验
                        files.push(file);
                        resolve();
                    });
                } else if (item.isDirectory) {
                    const dirReader = item.createReader();
                    dirReader.readEntries(async (entries) => {
                        for (let i = 0; i < entries.length; i++) {
                            await this.traverseFileTree(entries[i], files);
                        }
                        resolve();
                    });
                } else {
                    resolve();
                }
            });
        },
        
        // --- 核心修改点：严格校验逻辑 ---
        async handleFiles(files) {
            if (!files || files.length === 0) return;
            
            // 1. 校验数量：如果数量超限，直接拒绝操作
            if (files.length > 200) {
                this.$message.error(`单次最多允许上传 200 张图片，您选择了 ${files.length} 个文件，已取消操作`);
                return;
            }
            
            // 2. 校验文件合法性：检查是否有非图片、GIF格式或超过20MB的文件
            const invalidFiles = files.filter(f =>
                f.size > 20 * 1024 * 1024 ||
                !f.type.startsWith('image/') ||
                f.type === 'image/gif' ||
                f.name.toLowerCase().endsWith('.gif')
            );
            
            if (invalidFiles.length > 0) {
                this.$message.error('存在非图片、GIF格式或单张超过20MB的文件，已取消本次操作');
                return;
            }
            
            // 3. 校验全部通过，进入上传队列
            await this.uploadQueue(files);
        },
        
        /**
         * 静默压缩图片
         */
        compressImage(file, maxEdge = 1500) {
            return new Promise((resolve, reject) => {
                // GIF 动图不作压缩，否则会变成静态图
                
                const img = new Image();
                const url = URL.createObjectURL(file);
                
                img.onload = () => {
                    URL.revokeObjectURL(url);
                    let {width, height} = img;
                    
                    if (width <= maxEdge && height <= maxEdge) {
                        return resolve(file);
                    }
                    
                    const ratio = maxEdge / Math.max(width, height);
                    width = Math.floor(width * ratio);
                    height = Math.floor(height * ratio);
                    
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    
                    if (file.type === 'image/jpeg') {
                        ctx.fillStyle = '#fff';
                        ctx.fillRect(0, 0, width, height);
                    }
                    
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            return resolve(file);
                        }
                        const newFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        resolve(newFile);
                    }, file.type, 0.85);
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    resolve(file); // 加载失败退回原文件
                };
                
                img.src = url;
            });
        },
        
        async uploadQueue(files) {
            this.isUploading = true;
            this.totalUploadFiles = files.length;
            const token = localStorage.getItem("token") || '';
            
            // 新增：统计成功与失败的数量
            let successCount = 0;
            let failCount = 0;
            
            for (let i = 0; i < files.length; i++) {
                this.currentUploadIndex = i + 1;
                this.uploadProgress = 0;
                let file = files[i];
                
                try {
                    // 1. 压缩处理
                    file = await this.compressImage(file, 1500);
                    
                    const formData = new FormData();
                    formData.append('image', file);
                    formData.append('_method', 'post');
                    formData.append('_token', token);
                    formData.append('job_id', '' + this.jobId);
                    
                    // 2. 执行上传
                    const res = await $http.post(api.colUrl('/ast-wardrobe-job/upload-image'), formData, {
                        onUploadProgress: (progressEvent) => {
                            if (progressEvent.total) {
                                this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            }
                        }
                    });
                    
                    const resData = res.data || {};
                    
                    // 3. 结果判断
                    if (resData.code !== 0) {
                        // 业务报错：记录失败，不抛出 upload 事件
                        console.error(`第 ${this.currentUploadIndex} 张图片上传失败: ${resData.msg || '未知错误'}`);
                        failCount++;
                    } else {
                        // 只有完全成功才增加成功计数并通知父组件
                        successCount++;
                        this.$emit('upload', resData);
                    }
                    
                } catch (error) {
                    // 网络或代码层面的严重异常
                    console.error(`第 ${this.currentUploadIndex} 张图片处理或上传异常:`, error);
                    failCount++;
                } finally {
                    // 4. 统一节流：无论成功失败，都必须休眠。防止异常时循环瞬间跑完导致请求雪崩
                    if (this.$sleep) await this.$sleep(1300);
                }
            }
            
            // 5. 恢复初始状态
            this.isUploading = false;
            this.uploadProgress = 0;
            this.currentUploadIndex = 0;
            
            // 6. 根据最终结果给予精准提示
            if (failCount === 0) {
                this.$message.success(`上传完成，共成功上传 ${successCount} 张图片`);
            } else {
                // 如果有失败的，使用 warning 提醒用户，避免满屏报错体验变差
                this.$message.warning(`上传结束：成功 ${successCount} 张，失败 ${failCount} 张`);
            }
            
            // 将汇总数据抛给父组件，方便父组件做进一步的刷新或提示处理
            this.$emit('upload-complete', { successCount, failCount, total: files.length });
        }
    },
}
</script>

<style lang="scss" scoped>
/* 样式保持不变 */
.w-uploader {
    height: 300px;
    background: #FFFFFF;
    border: 1px dashed #DDDDDD;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s;
    
    &.is-dragover {
        border-color: #409EFF;
        background: #F2F8FE;
    }
    
    &.is-uploading {
        cursor: not-allowed;
    }
    
    img {
        width: 110px;
        height: 110px;
    }
    
    .text-1 {
        margin-top: 20px;
        font-weight: 400;
        font-size: 14px;
        color: #333333;
    }
    
    .text-2 {
        margin-top: 10px;
        font-weight: 400;
        font-size: 12px;
        color: #999999;
    }
    
    .progress-box {
        width: 80%;
        display: flex;
        flex-direction: column;
        align-items: center;
        
        .progress-bar {
            width: 100%;
            height: 10px;
            background: #EBEBEB;
            border-radius: 5px;
            margin-top: 20px;
            overflow: hidden;
            
            .progress-inner {
                height: 100%;
                background: #409EFF;
                border-radius: 5px;
                transition: width 0.2s ease-in-out;
            }
        }
    }
}
</style>